import type { ExperienceBlockDraft, ExperienceVariable } from '@matter_extractor/shared';
import { type ReactNode, useId } from 'react';
import { READER_TEST_IDS, type ReaderMode } from '../contracts';
import { READER_VARIABLE_CLASS, readerPlainText, readerPreviewSegments } from '../reader-text';

export interface CheckboxBlockProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'checkbox' }>;
  readonly mode: ReaderMode;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly resolveVariable: ((variable: ExperienceVariable) => string | null) | undefined;
  /** A preview NAMES its variables rather than resolving them (D4, D5) - see `reader-text.ts`. */
  readonly variableNames: ((variable: ExperienceVariable) => string) | undefined;
}

/**
 * The Checkbox block (§9.2): a label, obligatory or not, and a variable name.
 *
 * Its label is the ONE plain text of a page that accepts variables - §9.2 says
 * « libellé (variables acceptées) » of the checkbox and of nothing else - so it
 * goes through `readerPlainText` where a Bouton's and an Input's labels are
 * rendered verbatim. A preview draws each token as a pill in the theme's
 * accent, carrying its name (D5): React owns this label, so the pill is a span
 * and no markup is assembled. A preview that also hands a resolver - the
 * full-screen preview of #141 - gets the VALUE for the tokens it answers and
 * the pill for the rest; the session is told apart by `variableNames` being
 * absent, never by the resolver being present.
 *
 * `readOnly` outside `interactive` for the reason `input-block.tsx` gives, and
 * because React refuses a controlled `checked` with no `onChange` unless it is
 * told the field is read-only.
 */
export function CheckboxBlock({
  block,
  mode,
  checked,
  onChange,
  resolveVariable,
  variableNames,
}: CheckboxBlockProps): ReactNode {
  const id = useId();
  const readOnly = mode !== 'interactive';

  const label =
    variableNames !== undefined
      ? readerPreviewSegments(block.label, variableNames, resolveVariable).map((segment, index) =>
          segment.kind === 'text' ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: a run of literal text has no id, and two identical runs are two distinct places in one label
            <span key={`text-${index}`}>{segment.text}</span>
          ) : (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: the same token may appear twice, and each occurrence is its own pill
              key={`variable-${index}`}
              className={READER_VARIABLE_CLASS}
              data-testid={READER_TEST_IDS.variable}
            >
              {segment.label}
            </span>
          ),
        )
      : readerPlainText(block.label, resolveVariable);

  return (
    <div className="lecteur-case">
      <input
        id={id}
        type="checkbox"
        className="lecteur-case-carre"
        required={block.required}
        readOnly={readOnly}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}
